import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MailRecoveryListResponse,
  MailRecoveryMessageDetail,
  MailRecoveryMutationResponse,
  MailRecoverySettings,
  type MailRecoveryRetentionDays,
} from '@email/shared';
import { api } from './client.js';
import { mailKeys, settingsInvalidations, settingsKeys } from './query-cache.js';

export function useMailRecoverySettings() {
  return useQuery({
    queryKey: settingsKeys.mailRecoverySettings,
    queryFn: () => api('/api/mail/recovery/settings', { schema: MailRecoverySettings }),
  });
}

export function useMailRecoveryList(query: string) {
  return useInfiniteQuery({
    queryKey: mailKeys.recoveryList(query),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (query.trim()) params.set('q', query.trim());
      if (pageParam) params.set('cursor', pageParam);
      return api(`/api/mail/recovery?${params.toString()}`, {
        schema: MailRecoveryListResponse,
      });
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function useMailRecoveryDetail(id: number | null) {
  return useQuery({
    queryKey: mailKeys.recoveryDetail(id ?? 0),
    queryFn: () =>
      api(`/api/mail/recovery/${id}`, { schema: MailRecoveryMessageDetail }),
    enabled: id !== null,
  });
}

export function useUpdateMailRecoverySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (retentionDays: MailRecoveryRetentionDays) =>
      api('/api/mail/recovery/settings', {
        method: 'PUT',
        body: { retentionDays },
        schema: MailRecoverySettings,
      }),
    onSettled: () => settingsInvalidations.mailRecovery.invalidate(queryClient),
  });
}

export function useRestoreMailRecovery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetFolderId }: { id: number; targetFolderId: number }) =>
      api(`/api/mail/recovery/${id}/restore`, {
        method: 'POST',
        body: { targetFolderId },
        schema: MailRecoveryMutationResponse,
      }),
    onSettled: () => settingsInvalidations.mailRecovery.invalidate(queryClient),
  });
}

export function usePurgeMailRecovery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/mail/recovery/${id}`, {
        method: 'DELETE',
        schema: MailRecoveryMutationResponse,
      }),
    onSettled: () => settingsInvalidations.mailRecovery.invalidate(queryClient),
  });
}

export function usePurgeAllMailRecovery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api('/api/mail/recovery', {
        method: 'DELETE',
        schema: MailRecoveryMutationResponse,
      }),
    onSettled: () => settingsInvalidations.mailRecovery.invalidate(queryClient),
  });
}
